/* global ve */
import { Amplify } from 'aws-amplify'
import {
  signOut,
  fetchAuthSession,
  signInWithRedirect
} from 'aws-amplify/auth'
import {
  doLog,
  initVeEnvLoc,
  getVeEnvLoc,
  getVeEnvLocAwsRegion,
  getVeEnvLocIdentityPoolId,
  getVeEnvLocUserPoolId,
  getVeEnvLocCognitoAppClientId,
  getVeEnvLocCognitoAppWebDomain,
  getCognitoRedirectUri,
  hasKeys,
  parseJwt,
  redirectToLogin
} from './veb.js'
import cfg from './vecfg.js'
const vecfg = cfg.cfg

const getAmplifyConfig = (veEnvLoc, dbg = false) => { // not used atm?
  const fn = 'getAmplifyConfig'
  dbg = true
  const redirectSignInUri = getCognitoRedirectUri(dbg) // Get it once
  const amplifyConfig = {
    Auth: {
      Cognito: {
        region: getVeEnvLocAwsRegion(vecfg, veEnvLoc, dbg),
        identityPoolId: getVeEnvLocIdentityPoolId(vecfg, veEnvLoc, dbg),
        userPoolId: getVeEnvLocUserPoolId(vecfg, veEnvLoc, dbg),
        userPoolClientId: getVeEnvLocCognitoAppClientId(vecfg, veEnvLoc, dbg),
        loginWith: {
          oauth: {
            domain: getVeEnvLocCognitoAppWebDomain(vecfg, veEnvLoc, dbg),
            scopes: ['email', 'openid'],
            redirectSignIn: redirectSignInUri, // Use the variable
            redirectSignOut: redirectSignInUri, // Use the variable
            responseType: 'code'
          }
        }
      }
    }
  }
  doLog(fn, { amplifyConfig, configuredRedirectSignIn: redirectSignInUri, amplifyConfigTxt: JSON.stringify(amplifyConfig) }, dbg) // Added this line
  return amplifyConfig
}

const getCurrentAmplifyConfig = (Auth, dbg = false) => { // get the current config object
  const fn = 'getCurrentAmplifyConfig'
  dbg = true
  try {
    const currentConfig = Auth.configure()
    doLog(fn, { currentConfig }, dbg)
    return currentConfig
  } catch (e) {
    doLog(fn, { error: e }, true)
  }
}

let amplifyConfigured = false

const initAmplifyConfig = (thisEnvLoc, dbg = false) => {
  const fn = 'initAmplifyConfig'
  dbg = true
  doLog(fn, { thisEnvLoc, amplifyConfigured }, dbg)
  // init ve environment
  if (!thisEnvLoc) thisEnvLoc = 've-cd-dev-in-1' // default
  initVeEnvLoc(thisEnvLoc, dbg) // init environment
  const veEnvLoc = getVeEnvLoc(dbg)
  doLog(fn, { veEnvLoc }, dbg)
  if (amplifyConfigured) {
    doLog(fn, { info: 'Amplify already configured, skipping.' }, dbg)
    return Amplify.getConfig() // Return current config if already configured
  }
  const redirectSignInUri = getCognitoRedirectUri(dbg) // Get it once
  const amplifyConfig = {
    Auth: {
      Cognito: {
        region: getVeEnvLocAwsRegion(vecfg, veEnvLoc, dbg), // REQUIRED - Amazon Cognito Region
        identityPoolId: getVeEnvLocIdentityPoolId(vecfg, veEnvLoc, dbg), // OPTIONAL - Amazon Cognito Identity Pool ID
        userPoolId: getVeEnvLocUserPoolId(vecfg, veEnvLoc, dbg), // REQUIRED - Amazon Cognito User Pool ID
        userPoolClientId: getVeEnvLocCognitoAppClientId(vecfg, veEnvLoc, dbg), // REQUIRED - Amazon Cognito Web Client ID (renamed from userPoolWebClientId)
        loginWith: { // OPTIONAL - Hosted UI configuration
          oauth: {
            domain: getVeEnvLocCognitoAppWebDomain(vecfg, veEnvLoc, dbg),
            scopes: ['email', 'openid'], // 'scope' is now 'scopes' (plural)
            redirectSignIn: [redirectSignInUri], // 'redirectSignIn' is now an array
            redirectSignOut: [redirectSignInUri], // 'redirectSignOut' is now an array
            responseType: 'code'
          }
        }
      }
    }
  }
  doLog(fn, { amplifyConfig, configuredRedirectSignIn: redirectSignInUri, amplifyConfigTxt: JSON.stringify(amplifyConfig) }, dbg)
  Amplify.configure(amplifyConfig)
  amplifyConfigured = true // Set flag after configuration
  return amplifyConfig
}

const doAmplifySignIn = async (Amplify, Auth, veEnvLoc, dbg = false) => {
  const fn = 'doAmplifySignIn'
  dbg = true
  doLog(fn, { veEnvLoc }, dbg)
  // 1. Configure Amplify FIRST
  const amplifyConfig = initAmplifyConfig(veEnvLoc, dbg)
  Amplify.configure(amplifyConfig)
  doLog(fn, { amplifyConfig }, dbg)
  // 2. Just call signInWithRedirect
  // This one function handles everything:
  // - Reads the config
  // - Builds the URL
  // - Redirects the user
  // - Sets up the handler to catch the code on the return
  try {
    doLog(fn, { info: 'Attempting redirect via Amplify.signInWithRedirect' }, dbg)
    await signInWithRedirect()
  } catch (e) {
    doLog(fn, { info: 'Error during signInWithRedirect', error: e }, true)
  }
}

let session

const setSession = (newSession, dbg = false) => {
  const fn = 'setSession'
  const veEnvLoc = getVeEnvLoc(dbg)
  doLog(fn, { newSessionFromFetch: newSession, hasTokens: newSession?.tokens }, dbg) // Added hasTokens for clarity
  if (newSession && newSession.tokens) {
    session = newSession
    doLog(fn, { sessionSet: session, accessTokenExists: !!session.tokens.accessToken, refreshTokenExists: !!session.tokens.refreshToken, idTokenExists: !!session.tokens.idToken }, dbg) // More detailed logging
    if (session.tokens.accessToken) {
      const accessToken = session.tokens.accessToken.toString()
      window.localStorage.setItem(veEnvLoc + 'AccessToken', accessToken)
    }
    if (session.tokens.refreshToken) {
      const refreshToken = session.tokens.refreshToken.toString()
      window.localStorage.setItem(veEnvLoc + 'RefreshToken', refreshToken)
    }
    if (session.tokens.idToken) {
      const idToken = session.tokens.idToken.toString()
      window.localStorage.setItem(veEnvLoc + 'IdToken', idToken)
    }
  } else {
    session = null
    doLog(fn, { info: 'invalid session or no tokens, session cleared', newSession }, true)
  }
}

const isValidSession = (thisSession, dbg = false) => {
  const fn = 'isValidSession'
  const isValid = hasKeys(thisSession?.tokens) // accessToken, refreshToken, idToken
  doLog(fn, { isValid }, dbg)
  return isValid
}

const getSession = (dbg = false) => {
  const fn = 'getSession'
  doLog(fn, { session }, dbg)
  return isValidSession(session, dbg) ? session : null
}

const getAmplifyAccessToken = async (dbg = false) => {
  const fn = 'getAmplifyAccessToken'
  try {
    const session = await fetchAuthSession()
    if (isValidSession(session, dbg)) {
      setSession(session, dbg) // Keep local storage and session variable in sync
      const accessToken = session.tokens.accessToken.toString()
      doLog(fn, { accessTokenLength: accessToken.length }, dbg)
      return accessToken
    }
    return null
  } catch (e) {
    doLog(fn, { error: e }, true)
    return null
  }
}

const getAmplifyRefreshToken = async (dbg = false) => {
  const fn = 'getAmplifyRefreshToken'
  try {
    const session = await fetchAuthSession()
    if (isValidSession(session, dbg)) {
      setSession(session, dbg)
      const refreshToken = session.tokens.refreshToken.toString()
      if (refreshToken) doLog(fn, { refreshTokenLength: refreshToken.length }, dbg)
      return refreshToken
    }
    return null
  } catch (e) {
    doLog(fn, { error: e }, true)
    return null
  }
}

const getAmplifyIdToken = async (dbg = false) => {
  const fn = 'getAmplifyIdToken'
  try {
    const session = await fetchAuthSession()
    if (isValidSession(session, dbg)) {
      setSession(session, dbg)
      const idToken = session.tokens.idToken.toString()
      doLog(fn, { idTokenLength: idToken.length }, dbg)
      return idToken
    }
    return null
  } catch (e) {
    doLog(fn, { error: e }, true)
    return null
  }
}

const refreshAmplifyToken = async (dbg = false) => {
  const fn = 'refreshAmplifyToken'
  dbg = true
  try {
    const session = await fetchAuthSession({ forceRefresh: true })
    if (isValidSession(session, dbg)) setSession(session)
    doLog(fn, { session }, dbg)
  } catch (e) {
    console.log('Unable to refresh Token', e)
  }
}

let authenticationIsInProgress = false

/**
 * Asynchronously checks if the user is authenticated.
 * Returns true if a valid session is found, false otherwise.
 */
const isAuthenticated = async (dbg = false) => {
  if (dbg) console.log('isAuthenticated: checking session...')
  try {
    // fetchAuthSession() will either get the session or throw an error
    // if the user is not signed in or tokens are invalid.
    const session = await fetchAuthSession()
    return isValidSession(session, dbg)
    // If we get here, the session is valid.
    // if (dbg) console.log('isAuthenticated: session is VALID', session)
    // if (authenticationIsInProgress) authenticationIsInProgress = false; // Your side-effect
    // return true
  } catch (error) { // This is the expected path for an unauthenticated user.
    if (dbg) console.log('isAuthenticated: no session found', error)
    return false
  }
}

const isNotAuthenticated = async (dbg = false) => {
  return !(await isAuthenticated(dbg))
}

const isAuthenticationInProgress = (dbg = true) => {
  if (dbg) console.log('isAuthenticationInProgress:authenticationIsInProgress: ' + authenticationIsInProgress)
  return authenticationIsInProgress
}

const getCognitoUsername = async (dbg = false) => {
  const jwtToken = await getAmplifyAccessToken(dbg) || null
  return jwtToken ? parseJwt(jwtToken).username || '' : ''
}

const getCognitoEmail = async (dbg = false) => {
  const jwtToken = await getAmplifyIdToken(dbg) || null
  return jwtToken ? parseJwt(jwtToken).email || '' : ''
}

const getCognitoGroups = async (dbg = false) => {
  const jwtToken = await getAmplifyAccessToken(dbg) || null
  return jwtToken ? parseJwt(jwtToken)['cognito:groups'] || [] : []
}

const isMemberOfCognitoGroup = async (cognitoGroup, dbg = false) => {
  const groups = await getCognitoGroups(dbg)
  return groups.includes(cognitoGroup)
}

const isMemberOfSatGroup = async (satGroupId, dbg = false) => {
  const isMember = await ve.veapi.isMemberOfSatGroup(satGroupId, dbg)
  return isMember
}

const isMemberOfGroup = async (group, dbg = false) => {
  let isMember = await isMemberOfCognitoGroup(group, dbg)
  if (isMember) return isMember
  isMember = await isMemberOfSatGroup(group, dbg)
  return isMember
}

const isMemberOfGroups = async (groups, dbg = false) => {
  const fn = 'isMemberOfGroups'
  const memberOf = []
  for (const group of groups) {
    const memberOfThisGroup = await isMemberOfGroup(group, dbg)
    doLog(fn, { group, memberOfThisGroup }, dbg)
    if (memberOfThisGroup) memberOf.push(group)
  }
  doLog(fn, { memberOf }, dbg)
  return (memberOf.length > 0)
}

const getUserProviderName = async (dbg = false) => {
  const fn = 'getUserProviderName'
  const defaultProviderName = 'Cognito'
  try {
    const jwtToken = await getAmplifyIdToken(dbg) || null
    const identities = jwtToken ? parseJwt(jwtToken).identities : null
    const providerName = identities[0].providerName
    doLog(fn, { providerName }, dbg)
    return providerName
  } catch (err) {
    doLog(fn, { defaultProviderName }, dbg)
    return defaultProviderName
  }
}

const startCognitoLogin = (thisEnvLoc, dbg = true) => {
  const fn = 'startCognitoLogin'
  authenticationIsInProgress = true
  doLog(fn, { authenticationIsInProgress, thisEnvLoc }, dbg)
  doAmplifySignIn(ve.Amplify, ve.Auth, thisEnvLoc, dbg)
}

const doCognitoLogout = async () => {
  try {
    // 1. Clear YOUR app-specific state (like userProfile).
    // Do NOT manually remove Amplify's tokens.
    window.sessionStorage.removeItem('userProfile')
    session = null // If 'session' is a global var in your app
    // 2. Call the v6 signOut() function.
    // This one function will:
    // - Clear Amplify's managed tokens from localStorage.
    // - Redirect the user to your Cognito logout URL.
    // - Cognito will then redirect back to your 'redirectSignOut' URL.
    await signOut()
    // 3. DO NOT call redirectToLogin() here.
    // The signOut() function *is* a full-page redirect.
    // This line will likely never be reached.
  } catch (error) {
    console.log('error signing out: ', error)
    // As a fallback, if signOut() fails (e.g., config error),
    // you can manually redirect to ensure the user lands on the login page.
    redirectToLogin()
  }
}

export default {
  getAmplifyConfig,
  getCurrentAmplifyConfig,
  initAmplifyConfig,
  doAmplifySignIn,
  setSession,
  getSession,
  getAmplifyAccessToken,
  getAmplifyRefreshToken,
  getAmplifyIdToken,
  refreshAmplifyToken,
  isAuthenticated,
  isNotAuthenticated,
  isAuthenticationInProgress,
  getCognitoUsername,
  getCognitoEmail,
  getCognitoGroups,
  isMemberOfCognitoGroup,
  isMemberOfSatGroup,
  isMemberOfGroup,
  isMemberOfGroups,
  getUserProviderName,
  startCognitoLogin,
  doCognitoLogout,
  fetchAuthSession
}
